import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from "@angular/core";

import { BaseComponent, UserService, HelperService, StorageKeys, StorageService, environment } from "@mypxplat/xplat/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AppService } from "@mypxplat/xplat/web/core";
import { filter, take, takeUntil } from "rxjs";
import { BreadcrumbItem } from "../fender-ui/layout-header/layout-header.component";
import { FuiSidebarFilterComponent, FuiFilterContentComponent, FilterItem } from "../fender-ui";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-account",
  templateUrl: "account.component.html",
  standalone: false,
})
export class AccountComponent extends BaseComponent implements OnInit, OnDestroy {
  public breadcrumbs: BreadcrumbItem[] = [];
  @ViewChild("fuiSidebarFilter") fuiSidebarFilter: FuiSidebarFilterComponent;
  public loading: boolean;
  public displayedSection: string;
  public env = environment;
  public sectionMap: any = {
    general: "general.profile",
    orders: "header.nav.orders",
    showcases: "com.showcases",
    gift_cards: "Gift Cards",
    addresses: "Address Book",
    plan: environment.studioOnePlusBrandName,
    play_plan: "Fender Play",
    notifications: "sphere.account.email_prefs",
    communication_preferences: "account.communication_preferences",
    notification_preferences: "account.account_notifications",
    account_notifications: "account.account_notifications",
    collaboration: "sphere.account.collaboration",
    storage: "sphere.account.storage",
  };

  // Public variables for template access (converted from methods)
  public accountNavItems: FilterItem[] = [];
  public selectedAccountItem: FilterItem | null = null;
  constructor(
    userService: UserService,
    public helperService: HelperService,
    public appService: AppService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _storage: StorageService
  ) {
    super(userService);
    this.updateAccountNavItems();
  }

  /**
   * Covers the account page while a return from Discord is still being forwarded on.
   *
   * The OAuth callback lands the browser on `/account/connections?discord=…` as a FULL page load,
   * so the app boots, the account module lazy-loads, this page renders, and only then does the
   * connections panel read the stashed destination and navigate away. That whole sequence is a
   * second or two of a page the member never asked for and is about to leave.
   *
   * Only when there is somewhere to forward them TO — a member who linked from the account page
   * itself has no stash and is staying here, so they see the page normally. Reading the stash
   * does not consume it; the connections panel still does that.
   */
  public discordReturnPending = false;
  private _discordReturnTimer: any;

  ngOnInit() {
    window.scrollTo(0, 0);
    this._resolveDiscordReturn();
    this.loading = true;
    this.userService.subDetails$
      .pipe(
        filter((item) => !!item),
        take(1)
      )
      .subscribe((result) => {
        this.loading = false;
        this.updateAccountNavItems();
      });

    this.userService.user$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateAccountNavItems();
    });

    this._activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const category = params["category"];
      // Connections used to be its own account section; it is now a block inside General.
      // The Discord OAuth callback still returns to /account/connections with its
      // ?discord=…&reason=… params, so redirect rather than 404 — and carry the params
      // across, because the connections panel reads them to report the link result.
      if (category === "connections") {
        this._router.navigate(["account", "general"], { queryParamsHandling: "preserve", replaceUrl: true });
        return;
      }
      this.displayedSection = category;
      this.breadcrumbs = [
        { label: "header.nav.home", routerLink: ["/home"] },
        { label: "fui-header.account", routerLink: ["/account/general"] },
        { label: (category ? (this.sectionMap[category] ?? this.sectionMap[category.replace(/-/g, "_")]) : null) ?? "general.profile" },
      ];
      this.updateAccountNavItems();
      this.updateSelectedAccountItem();
    });
  }

  private _resolveDiscordReturn() {
    const returningFromDiscord = !!this._activatedRoute.snapshot.queryParams["discord"];
    if (!returningFromDiscord || !this._storage.getItem(StorageKeys.DISCORD_LINK_RETURN_TO)) return;
    this.discordReturnPending = true;
    // Belt and braces: the forward happens in the connections panel, and if anything stops it
    // from running the member must not be left staring at a spinner over their own account.
    this._discordReturnTimer = setTimeout(() => (this.discordReturnPending = false), 8000);
  }

  changeCategory(cat) {
    this.breadcrumbs = [{ label: "header.nav.home", routerLink: ["/home"] }, { label: "fui-header.account", routerLink: ["/account/general"] }, { label: this.sectionMap[cat] }];
    this.displayedSection = cat;
    this._router.navigate(["account/", cat], { replaceUrl: true });
    this.updateSelectedAccountItem();
    // Close mobile modal if open
    if (this.fuiSidebarFilter) {
      this.fuiSidebarFilter.closeMobileModal();
    }
  }

  private updateAccountNavItems() {
    const items = [
      {
        id: "general",
        section: "general",
        label: "sphere.your_profile",
        show: true,
      },
      {
        id: "orders",
        section: "orders",
        label: "header.nav.orders",
        show: true,
      },
      {
        id: "play_plan",
        section: "play_plan",
        label: "Fender Play sphere.account.membership_billing",
        show: this.userService.subDetails?.play_subscription_normalized,
      },
      {
        id: "plan",
        section: "plan",
        label: (this.appService.app == "fender" ? "Fender Studio Pro+ " : "") + "sphere.account.membership_billing",
        show: true,
      },
      {
        id: "addresses",
        section: "addresses",
        label: "Address Book",
        show: this.appService.app == "fender",
      },
      {
        id: "gift-cards",
        section: "gift-cards",
        label: "Gift Cards",
        show: this.appService.app == "fender",
      },
      {
        id: "showcases",
        section: "showcases",
        label: "com.showcases",
        show: true,
      },
      {
        id: "notifications",
        section: "notifications",
        label: "sphere.account.email_prefs",
        show: true,
      },
      {
        id: "notification_preferences",
        section: "notification_preferences",
        label: "account.account_notifications",
        show: true,
      },
      {
        id: "collaboration",
        section: "collaboration",
        label: "sphere.account.collaboration",
        show: true,
      },
      {
        id: "storage",
        section: "storage",
        label: "sphere.account.storage",
        show: this.userService.user?.active_subscription,
      },
    ];

    this.accountNavItems = items.filter((item) => item.show);
    this.updateSelectedAccountItem();
  }

  /** FSP+ marketing upsell on Account → Plan (Fender only); billing UI stays in plan-stripe / plan-generic. */
  showFspPlanUpsell(): boolean {
    const d = this.userService.subDetails;
    if (!d) return false;
    return !d.subscription || !d.active;
  }

  shouldShowAccountItem(item: FilterItem): boolean {
    return item.show !== false;
  }

  onAccountItemSelected(item: FilterItem): void {
    this.changeCategory(item.section);
  }

  private updateSelectedAccountItem() {
    if (!this.displayedSection) {
      this.selectedAccountItem = null;
      return;
    }
    this.selectedAccountItem = this.accountNavItems.find((item) => item.section === this.displayedSection) || null;
  }

  override ngOnDestroy() {
    // The common case is that the forward fires long before this timer would; clearing it stops
    // a destroyed component being held alive for the remainder of the window.
    if (this._discordReturnTimer) clearTimeout(this._discordReturnTimer);
    super.ngOnDestroy();
  }
}
